import {ApplicationConfig, inject, LOCALE_ID, provideAppInitializer} from '@angular/core';
import {provideRouter} from '@angular/router';

import {routes} from './app.routes';
import {provideTranslateService} from '@ngx-translate/core';
import {provideHttpClient, withInterceptors} from '@angular/common/http';
import {provideTranslateHttpLoader} from '@ngx-translate/http-loader';
import {MdbNotificationService} from 'mdb-angular-ui-kit/notification';
import {MdbModalService} from 'mdb-angular-ui-kit/modal';
import {DatePipe, registerLocaleData} from '@angular/common';
import {authInterceptor} from '@core/interceptors/auth.interceptor';
import localeDeAt from '@angular/common/locales/de-AT';
import {MdbPopconfirmService} from 'mdb-angular-ui-kit/popconfirm';
import {provideAnimations} from '@angular/platform-browser/animations';
import {AuthService} from '@core/services/auth/auth.service';
import {DummyAuthService} from '@core/services/auth/dummy-auth.service';
import {HttpAuthService} from '@core/services/auth/http-auth.service';
import {CUSTOMER_PROFILE_FACADE} from './features/customer/profile/customer-profile.facade';
import {DummyCustomerProfileFacade} from './features/customer/profile/dummy-customer-profile.facade';
import {HttpCustomerProfileFacade} from './features/customer/profile/http-customer-profile.facade';
import {CUSTOMER_DEPOT_FACADE} from './features/customer/depot/customer-depot.facade';
import {DummyCustomerDepotFacade} from './features/customer/depot/dummy-customer-depot.facade';
import {HttpCustomerDepotFacade} from './features/customer/depot/http-customer-depot.facade';
import {TRANSACTION_REQUESTS_FACADE} from './features/customer/transactions/transaction-requests.facade';
import {DummyTransactionRequestsFacade} from './features/customer/transactions/dummy-transaction-requests.facade';
import {HttpTransactionRequestsFacade} from './features/customer/transactions/http-transaction-requests.facade';
import {DOCUMENTS_FACADE} from './features/customer/documents/documents.facade';
import {DummyDocumentsFacade} from './features/customer/documents/dummy-documents.facade';
import {HttpDocumentsFacade} from './features/customer/documents/http-documents.facade';
import {PURCHASE_ORDER_FACADE} from './features/customer/purchase-order/purchase-order.facade';
import {DummyPurchaseOrderFacade} from './features/customer/purchase-order/dummy-purchase-order.facade';
import {HttpPurchaseOrderFacade} from './features/customer/purchase-order/http-purchase-order.facade';
import {ADMIN_CUSTOMERS_FACADE} from './features/admin/customers/admin-customers.facade';
import {DummyAdminCustomersFacade} from './features/admin/customers/dummy-admin-customers.facade';
import {HttpAdminCustomersFacade} from './features/admin/customers/http-admin-customers.facade';
import {ADMIN_TRANSACTIONS_FACADE} from './features/admin/transactions/admin-transactions.facade';
import {DummyAdminTransactionsFacade} from './features/admin/transactions/dummy-admin-transactions.facade';
import {HttpAdminTransactionsFacade} from './features/admin/transactions/http-admin-transactions.facade';
import {ADMIN_PURCHASE_ORDERS_FACADE} from './features/admin/transactions/admin-purchase-orders.facade';
import {DummyAdminPurchaseOrdersFacade} from './features/admin/transactions/dummy-admin-purchase-orders.facade';
import {HttpAdminPurchaseOrdersFacade} from './features/admin/transactions/http-admin-purchase-orders.facade';
import {ADMIN_DOCUMENTS_FACADE} from './features/admin/documents/admin-documents.facade';
import {DummyAdminDocumentsFacade} from './features/admin/documents/dummy-admin-documents.facade';
import {HttpAdminDocumentsFacade} from './features/admin/documents/http-admin-documents.facade';
import {ADMIN_STORAGE_FEES_FACADE} from './features/admin/storage-fees/admin-storage-fees.facade';
import {ADMIN_AGIO_FACADE} from './features/admin/agio/admin-agio.facade';
import {HttpAdminAgioFacade} from './features/admin/agio/http-admin-agio.facade';
import {DummyAdminAgioFacade} from './features/admin/agio/dummy-admin-agio.facade';
import {ADMIN_INFO_MAIL_FACADE} from './features/admin/info-mail/admin-info-mail.facade';
import {HttpAdminInfoMailFacade} from './features/admin/info-mail/http-admin-info-mail.facade';
import {DummyAdminInfoMailFacade} from './features/admin/info-mail/dummy-admin-info-mail.facade';
import {DummyAdminStorageFeesFacade} from './features/admin/storage-fees/dummy-admin-storage-fees.facade';
import {HttpAdminStorageFeesFacade} from './features/admin/storage-fees/http-admin-storage-fees.facade';
import {AUDIT_LOG_SERVICE} from '@core/services/audit/audit-log.service';
import {DummyAuditLogService} from '@core/services/audit/dummy-audit-log.service';
import {HttpAuditLogService} from '@core/services/audit/http-audit-log.service';
import {resolveRuntimeDataSource} from '@core/config/runtime-data-source';
import {firstValueFrom} from 'rxjs';

// Register the locale data
registerLocaleData(localeDeAt);

// Sprint 6 S6-2: Runtime-Override aus `?dataSource=dummy`/`?dataSource=http`
// + sessionStorage erlaubt es, einzelne Playwright-Specs gegen die Dummy-
// Facades fahren zu lassen, obwohl der Build-Default seit Sprint 5c HTTP
// ist. `dataSource` und `authMode` werden gemeinsam umgeschaltet — Mixed-
// Mode war nie ein Anwendungsfall.
const runtimeDataSource = resolveRuntimeDataSource();
const isHttp = runtimeDataSource === 'http';

const authImplementation = isHttp ? HttpAuthService : DummyAuthService;
const customerProfileFacade = isHttp ? HttpCustomerProfileFacade : DummyCustomerProfileFacade;
const customerDepotFacade = isHttp ? HttpCustomerDepotFacade : DummyCustomerDepotFacade;
const transactionRequestsFacade = isHttp ? HttpTransactionRequestsFacade : DummyTransactionRequestsFacade;
const documentsFacade = isHttp ? HttpDocumentsFacade : DummyDocumentsFacade;
const purchaseOrderFacade = isHttp ? HttpPurchaseOrderFacade : DummyPurchaseOrderFacade;
const adminCustomersFacade = isHttp ? HttpAdminCustomersFacade : DummyAdminCustomersFacade;
const adminTransactionsFacade = isHttp ? HttpAdminTransactionsFacade : DummyAdminTransactionsFacade;
const adminPurchaseOrdersFacade = isHttp
  ? HttpAdminPurchaseOrdersFacade
  : DummyAdminPurchaseOrdersFacade;
const adminDocumentsFacade = isHttp ? HttpAdminDocumentsFacade : DummyAdminDocumentsFacade;
const adminStorageFeesFacade = isHttp ? HttpAdminStorageFeesFacade : DummyAdminStorageFeesFacade;
const adminAgioFacade = isHttp ? HttpAdminAgioFacade : DummyAdminAgioFacade;
const adminInfoMailFacade = isHttp ? HttpAdminInfoMailFacade : DummyAdminInfoMailFacade;
const auditLogService = isHttp ? HttpAuditLogService : DummyAuditLogService;

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    provideHttpClient(withInterceptors([authInterceptor])),
    provideAnimations(),
    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: '/assets/i18n/',
        suffix: '.json',
      }),
      lang: 'de',
      fallbackLang: 'de',
    }),
    {provide: AuthService, useClass: authImplementation},
    {provide: CUSTOMER_PROFILE_FACADE, useClass: customerProfileFacade},
    {provide: CUSTOMER_DEPOT_FACADE, useClass: customerDepotFacade},
    {provide: TRANSACTION_REQUESTS_FACADE, useClass: transactionRequestsFacade},
    {provide: DOCUMENTS_FACADE, useClass: documentsFacade},
    {provide: PURCHASE_ORDER_FACADE, useClass: purchaseOrderFacade},
    {provide: ADMIN_CUSTOMERS_FACADE, useClass: adminCustomersFacade},
    {provide: ADMIN_TRANSACTIONS_FACADE, useClass: adminTransactionsFacade},
    {provide: ADMIN_PURCHASE_ORDERS_FACADE, useClass: adminPurchaseOrdersFacade},
    {provide: ADMIN_DOCUMENTS_FACADE, useClass: adminDocumentsFacade},
    {provide: ADMIN_STORAGE_FEES_FACADE, useClass: adminStorageFeesFacade},
    {provide: ADMIN_AGIO_FACADE, useClass: adminAgioFacade},
    {provide: ADMIN_INFO_MAIL_FACADE, useClass: adminInfoMailFacade},
    {provide: AUDIT_LOG_SERVICE, useClass: auditLogService},
    provideAppInitializer(() => {
      // Session aus sessionStorage rehydrieren. Im dummy-Modus ein No-Op,
      // im http-Modus: Token → /api/me, bei 401/403 den Auth-State leeren.
      const authService = inject(AuthService);
      return firstValueFrom(authService.hydrate());
    }),
    {
      provide: LOCALE_ID,
      useValue: 'de-AT',
    },
    MdbNotificationService,
    MdbModalService,
    MdbPopconfirmService,
    DatePipe,
  ],
};
